<template>
    <b-modal
        :id="modalId"
        :title="title"
        size="xl"
        hide-footer
        no-close-on-backdrop
        no-enforce-focus
        @show="onModalShow"
        @hidden="onModalHidden">
        <template #modal-header>
            <h5 class="modal-title flex-grow-1">{{ title }}</h5>
            <b-button
                v-if="hasPermission"
                variant="primary"
                size="sm"
                class="mr-3"
                :disabled="saving || !isLoaded"
                @click="saveAnnotatedPdf">
                <i v-if="saving" class="fas fa-spinner fa-spin"></i>
                <i v-else class="fas fa-save"></i>
                Save
            </b-button>
            <button type="button" class="close" aria-label="Close" @click="handleCloseRequest">
                <span aria-hidden="true">&times;</span>
            </button>
        </template>

        <!-- Unsaved Changes Warning Modal -->
        <b-modal
            :id="`${modalId}-unsaved-warning`"
            title="Unsaved Changes"
            ok-title="Discard Changes"
            ok-variant="danger"
            cancel-title="Go Back"
            @ok="discardAndClose"
            @cancel="showUnsavedWarning = false"
            @hidden="showUnsavedWarning = false"
            v-model="showUnsavedWarning"
            centered>
            <p>You have unsaved changes to this PDF. Are you sure you want to close without saving?</p>
        </b-modal>

        <div v-if="loading" class="text-center p-5">
            <i class="fas fa-spinner fa-spin fa-3x"></i>
            <p class="mt-3">Loading PDF...</p>
        </div>

        <div v-else-if="error" class="alert alert-danger">
            {{ error }}
        </div>

        <div v-else>
            <!-- Toolbar -->
            <div class="annotation-toolbar mb-2 p-2 bg-white rounded d-flex flex-wrap align-items-center">
                <b-button-group size="sm" class="mr-3">
                    <b-button
                        :variant="currentTool === 'select' ? 'primary' : 'outline-dark'"
                        @click="setTool('select')"
                        title="Select">
                        <i class="fas fa-mouse-pointer"></i>
                    </b-button>
                    <b-button
                        v-if="hasPermission"
                        :variant="currentTool === 'text' ? 'primary' : 'outline-dark'"
                        @click="setTool('text')"
                        title="Add Text">
                        <i class="fas fa-font"></i>
                    </b-button>
                    <b-button
                        v-if="hasPermission"
                        :variant="currentTool === 'rect' ? 'primary' : 'outline-dark'"
                        @click="setTool('rect')"
                        title="Rectangle">
                        <i class="fas fa-square"></i>
                    </b-button>
                </b-button-group>

                <b-button
                    v-if="hasPermission"
                    variant="outline-danger"
                    size="sm"
                    class="mr-3"
                    :disabled="!hasSelection"
                    @click="deleteSelected"
                    title="Delete Selected">
                    <i class="fas fa-trash"></i>
                </b-button>

                <!-- Layer controls -->
                <b-button-group v-if="hasPermission && hasSelection" size="sm" class="mr-3">
                    <b-button variant="outline-dark" @click="bringToFront" title="Bring to Front">
                        <i class="fas fa-angle-double-up"></i>
                    </b-button>
                    <b-button variant="outline-dark" @click="bringForward" title="Bring Forward">
                        <i class="fas fa-angle-up"></i>
                    </b-button>
                    <b-button variant="outline-dark" @click="sendBackward" title="Send Backward">
                        <i class="fas fa-angle-down"></i>
                    </b-button>
                    <b-button variant="outline-dark" @click="sendToBack" title="Send to Back">
                        <i class="fas fa-angle-double-down"></i>
                    </b-button>
                </b-button-group>

                <div v-if="hasPermission" class="d-flex align-items-center mr-3">
                    <label class="mb-0 mr-2 small">Color:</label>
                    <input
                        type="color"
                        v-model="strokeColor"
                        class="form-control form-control-sm color-picker"
                        @input="applyColorToSelection"
                        title="Stroke/Text Color" />
                </div>

                <div v-if="hasPermission && showFontSizeControl" class="d-flex align-items-center mr-3">
                    <label class="mb-0 mr-2 small">Size:</label>
                    <b-form-select
                        v-model="fontSize"
                        :options="fontSizeOptions"
                        size="sm"
                        class="font-size-select"
                        @change="applyFontSizeToSelection" />
                </div>

                <div v-if="hasPermission && currentTool === 'rect'" class="d-flex align-items-center mr-3">
                    <label class="mb-0 mr-2 small">Fill:</label>
                    <input
                        type="color"
                        v-model="fillColor"
                        class="form-control form-control-sm color-picker"
                        :disabled="noFill"
                        @input="applyFillToSelection" />
                    <b-form-checkbox v-model="noFill" class="ml-2 small" @change="applyFillToSelection">
                        No Fill
                    </b-form-checkbox>
                </div>

                <b-button
                    v-if="hasPermission"
                    variant="outline-dark"
                    size="sm"
                    class="mr-3"
                    :disabled="!canUndo"
                    @click="undo"
                    title="Undo (Ctrl+Z)">
                    <i class="fas fa-undo"></i>
                </b-button>

                <!-- Page navigation -->
                <div v-if="totalPages > 1" class="d-flex align-items-center ml-auto">
                    <b-button
                        variant="outline-dark"
                        size="sm"
                        :disabled="currentPage <= 1"
                        @click="goToPage(currentPage - 1)">
                        <i class="fas fa-chevron-left"></i>
                    </b-button>
                    <span class="mx-2 small">Page {{ currentPage }} of {{ totalPages }}</span>
                    <b-button
                        variant="outline-dark"
                        size="sm"
                        :disabled="currentPage >= totalPages"
                        @click="goToPage(currentPage + 1)">
                        <i class="fas fa-chevron-right"></i>
                    </b-button>
                </div>
            </div>

            <!-- Canvas Container -->
            <div class="canvas-container-wrapper" ref="canvasWrapper">
                <div class="canvas-container" ref="canvasContainer">
                    <canvas ref="pdfCanvas"></canvas>
                    <canvas ref="fabricCanvas"></canvas>
                </div>
            </div>
        </div>
    </b-modal>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch, Emit } from 'vue-property-decorator';
import axios from 'axios';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { Permission } from '@/models/Permission';

// Library types (loaded dynamically)
declare const pdfjsLib: any;
declare const fabric: any;
declare const PDFLib: any;

interface AnnotationState {
    objects: any[];
}

@Component({
    name: 'PdfAnnotationModal',
})
export default class PdfAnnotationModal extends Vue {
    @Prop({ default: null })
    readonly imageId!: number | null;

    @Prop({ default: null })
    readonly escriptId!: number | null;

    @Prop({ default: 'pdf-annotation-modal' })
    readonly modalId!: string;

    @Prop({ default: 'Annotate PDF' })
    readonly title!: string;

    @Prop({ default: false })
    readonly requirePermission!: boolean;

    @Prop({ default: null })
    readonly initialText!: string | null;

    @Prop({ default: false })
    readonly autoPlace!: boolean;

    // State
    private loading = false;
    private saving = false;
    private error = '';
    private isLoaded = false;
    private librariesLoaded = false;

    // PDF state
    private pdfDoc: any = null;
    private pdfBytes: Uint8Array | null = null;
    private currentPage = 1;
    private totalPages = 0;
    private scale = 1.5;

    // Fabric canvas
    private fabricCanvas: any = null;
    private currentTool = 'select';
    private hasSelection = false;

    // Colors
    private strokeColor = '#ff0000';
    private fillColor = '#ffffff';
    private noFill = true;

    // Font size
    private fontSize = 16;
    private readonly fontSizeOptions = [
        { value: 8, text: '8' },
        { value: 10, text: '10' },
        { value: 12, text: '12' },
        { value: 14, text: '14' },
        { value: 16, text: '16' },
        { value: 18, text: '18' },
        { value: 20, text: '20' },
        { value: 24, text: '24' },
        { value: 28, text: '28' },
        { value: 32, text: '32' },
        { value: 36, text: '36' },
        { value: 48, text: '48' },
    ];

    // Rectangle drawing state
    private isDrawingRect = false;
    private rectStartPoint: { x: number; y: number } | null = null;
    private tempRect: any = null;

    // Annotations and undo
    private pageAnnotations: Record<number, AnnotationState> = {};
    private undoStacks: Record<number, string[]> = {};
    private isUndoing = false;
    private readonly MAX_UNDO_STATES = 50;
    private hasUnsavedChanges = false;
    private showUnsavedWarning = false;

    $refs!: {
        canvasWrapper: HTMLDivElement;
        canvasContainer: HTMLDivElement;
        pdfCanvas: HTMLCanvasElement;
        fabricCanvas: HTMLCanvasElement;
    };

    get hasPermission(): boolean {
        if (!this.requirePermission) return true;
        return (this.$permissions as Permission[]).some(
            (p) => p.permissionCodeName === 'Maintenance/Hardcopy Note'
        );
    }

    get canUndo(): boolean {
        const stack = this.undoStacks[this.currentPage];
        return stack && stack.length > 1;
    }

    get showFontSizeControl(): boolean {
        if (this.currentTool === 'text') return true;
        const obj = this.fabricCanvas?.getActiveObject();
        return obj && (obj.type === 'i-text' || obj.type === 'text');
    }

    get currentUserName(): string {
        return (this as any).$user?.userName || 'Unknown';
    }

    @Emit('saved')
    emitSaved(payload: { oldImageId: number | null; newImageId: number; escriptId: number | null }) {
        return payload;
    }

    @Emit('closed')
    emitClosed() {
        return;
    }

    async onModalShow() {
        this.resetState();
        await this.loadLibraries();
        if (this.librariesLoaded) {
            await this.loadPdf();
        }
        this.setupKeyboardShortcuts();
    }

    onModalHidden() {
        this.cleanup();
        this.emitClosed();
    }

    private resetState() {
        this.loading = false;
        this.saving = false;
        this.error = '';
        this.isLoaded = false;
        this.pdfDoc = null;
        this.pdfBytes = null;
        this.currentPage = 1;
        this.totalPages = 0;
        this.fabricCanvas = null;
        this.currentTool = 'select';
        this.hasSelection = false;
        this.pageAnnotations = {};
        this.undoStacks = {};
        this.isUndoing = false;
        this.hasUnsavedChanges = false;
        this.showUnsavedWarning = false;
    }

    private cleanup() {
        if (this.fabricCanvas) {
            this.fabricCanvas.dispose();
            this.fabricCanvas = null;
        }
        this.removeKeyboardShortcuts();
    }

    private async loadLibraries(): Promise<void> {
        if (this.librariesLoaded) return;

        const scripts = [
            { src: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js', global: 'pdfjsLib' },
            { src: 'https://cdnjs.cloudflare.com/ajax/libs/fabric.js/5.3.1/fabric.min.js', global: 'fabric' },
            { src: 'https://unpkg.com/pdf-lib@1.17.1/dist/pdf-lib.min.js', global: 'PDFLib' },
        ];

        try {
            for (const script of scripts) {
                if (!(window as any)[script.global]) {
                    await this.loadScript(script.src);
                }
            }

            // Set PDF.js worker
            if (typeof pdfjsLib !== 'undefined') {
                pdfjsLib.GlobalWorkerOptions.workerSrc =
                    'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
            }

            this.librariesLoaded = true;
        } catch (err) {
            console.error('Failed to load PDF libraries:', err);
            this.error = 'Failed to load PDF annotation libraries.';
        }
    }

    private loadScript(src: string): Promise<void> {
        return new Promise((resolve, reject) => {
            const existing = document.querySelector(`script[src="${src}"]`);
            if (existing) {
                resolve();
                return;
            }

            const script = document.createElement('script');
            script.src = src;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
            document.head.appendChild(script);
        });
    }

    private async loadPdf(): Promise<void> {
        this.loading = true;
        this.error = '';

        try {
            // Determine the endpoint based on whether we have an imageId or escriptId
            let endpoint: string;
            if (this.escriptId) {
                endpoint = `/api/Escript/${this.escriptId}/image`;
            } else if (this.imageId) {
                endpoint = `/api/Image/${this.imageId}`;
            } else {
                throw new Error('No image ID or eScript ID provided.');
            }

            const response = await axios.get(endpoint, {
                responseType: 'arraybuffer',
            });

            this.pdfBytes = new Uint8Array(response.data);
            this.pdfDoc = await pdfjsLib.getDocument({ data: this.pdfBytes.slice() }).promise;
            this.totalPages = this.pdfDoc.numPages;
            this.pageAnnotations = {};
            this.undoStacks = {};

            // Set loading to false first so Vue renders the canvas elements
            this.loading = false;
            await this.$nextTick();

            await this.renderPage(1);
            this.isLoaded = true;

            // Auto-place initial text if provided
            if (this.initialText && this.autoPlace) {
                await this.$nextTick();
                this.autoPlaceText(this.initialText);
            }
        } catch (err: any) {
            console.error('Error loading PDF:', err);
            this.error = err?.response?.data?.message || err?.message || 'Failed to load PDF.';
            this.loading = false;
        }
    }

    private async renderPage(pageNum: number): Promise<void> {
        if (!this.pdfDoc) return;

        // Save current page annotations before switching
        if (this.fabricCanvas && this.currentPage !== pageNum) {
            this.pageAnnotations[this.currentPage] = this.fabricCanvas.toJSON();
        }

        const page = await this.pdfDoc.getPage(pageNum);
        const viewport = page.getViewport({ scale: this.scale });

        const pdfCanvas = this.$refs.pdfCanvas;
        const ctx = pdfCanvas.getContext('2d');

        pdfCanvas.width = viewport.width;
        pdfCanvas.height = viewport.height;

        await page.render({
            canvasContext: ctx,
            viewport: viewport,
        }).promise;

        // Initialize/resize Fabric canvas
        this.initFabricCanvas(viewport.width, viewport.height);

        // Load saved annotations for this page
        if (this.pageAnnotations[pageNum]) {
            this.fabricCanvas.loadFromJSON(this.pageAnnotations[pageNum], () => {
                this.fabricCanvas.renderAll();
                if (!this.undoStacks[pageNum] || this.undoStacks[pageNum].length === 0) {
                    this.undoStacks[pageNum] = [JSON.stringify(this.fabricCanvas.toJSON())];
                }
            });
        } else {
            this.undoStacks[pageNum] = [JSON.stringify(this.fabricCanvas.toJSON())];
        }

        this.currentPage = pageNum;
        this.setTool(this.currentTool);
    }

    private initFabricCanvas(width: number, height: number): void {
        const fabricCanvasEl = this.$refs.fabricCanvas;

        if (this.fabricCanvas) {
            this.fabricCanvas.dispose();
        }

        fabricCanvasEl.width = width;
        fabricCanvasEl.height = height;

        this.fabricCanvas = new fabric.Canvas(fabricCanvasEl, {
            width: width,
            height: height,
            selection: true,
        });

        // Position the fabric canvas wrapper
        const wrapper = fabricCanvasEl.parentElement;
        if (wrapper && wrapper.classList.contains('canvas-container')) {
            const fabricWrapper = this.fabricCanvas.wrapperEl;
            if (fabricWrapper) {
                fabricWrapper.style.position = 'absolute';
                fabricWrapper.style.top = '0';
                fabricWrapper.style.left = '0';
            }
        }

        // Event handlers
        this.fabricCanvas.on('mouse:dblclick', (e: any) => {
            if (e.target && e.target.type === 'i-text') {
                e.target.enterEditing();
            }
        });

        this.fabricCanvas.on('mouse:down', this.onMouseDown.bind(this));
        this.fabricCanvas.on('mouse:move', this.onMouseMove.bind(this));
        this.fabricCanvas.on('mouse:up', this.onMouseUp.bind(this));

        // Track changes for undo
        this.fabricCanvas.on('object:added', () => {
            if (!this.isDrawingRect) {
                this.saveUndoState();
            }
        });
        this.fabricCanvas.on('object:removed', this.saveUndoState.bind(this));
        this.fabricCanvas.on('object:modified', this.saveUndoState.bind(this));
        this.fabricCanvas.on('text:changed', this.saveUndoState.bind(this));

        // Selection events
        this.fabricCanvas.on('selection:created', this.onSelectionChanged.bind(this));
        this.fabricCanvas.on('selection:updated', this.onSelectionChanged.bind(this));
        this.fabricCanvas.on('selection:cleared', this.onSelectionChanged.bind(this));
    }

    private onSelectionChanged(): void {
        const obj = this.fabricCanvas?.getActiveObject();
        this.hasSelection = !!obj;

        if (obj) {
            // Update color pickers to reflect selected object
            if (obj.type === 'i-text' || obj.type === 'text') {
                this.strokeColor = obj.fill || '#000000';
                this.fontSize = obj.fontSize || 16;
            } else if (obj.type === 'rect') {
                this.strokeColor = obj.stroke || '#000000';
                if (obj.fill && obj.fill !== 'transparent') {
                    this.fillColor = obj.fill;
                    this.noFill = false;
                } else {
                    this.noFill = true;
                }
            }
        }
    }

    private getCurrentColors() {
        return {
            stroke: this.strokeColor,
            fill: this.fillColor,
            noFill: this.noFill,
        };
    }

    private addAnnotationStamp(parentObj: any, offsetY: number): void {
        const now = new Date();
        const dateStr = now.toLocaleDateString() + ' ' + now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        const stampText = `${this.currentUserName} - ${dateStr}`;

        // Calculate stamp font size - roughly 60% of parent font size, with floor of 12pt
        let stampFontSize = 12;
        if (parentObj.type === 'i-text' || parentObj.type === 'text') {
            stampFontSize = Math.max(12, Math.round((parentObj.fontSize || 16) * 0.6));
        } else if (parentObj.type === 'rect') {
            // For rectangles, base it on height with floor of 12pt
            stampFontSize = Math.max(12, Math.round(parentObj.height * 0.15));
        }

        const stamp = new fabric.Text(stampText, {
            left: parentObj.left,
            top: parentObj.top + offsetY,
            fontSize: stampFontSize,
            fill: '#666666',
            fontFamily: 'Arial',
            fontStyle: 'italic',
            selectable: false,
            evented: false,
            hoverCursor: 'default',
        });

        // Store reference to stamp on parent and vice versa
        parentObj.annotationStamp = stamp;
        stamp.parentAnnotation = parentObj;
        stamp.stampOffsetY = offsetY;

        // When parent moves, move stamp with it
        parentObj.on('moving', () => {
            if (parentObj.annotationStamp) {
                parentObj.annotationStamp.set({
                    left: parentObj.left,
                    top: parentObj.top + this.getStampOffset(parentObj),
                });
                parentObj.annotationStamp.setCoords();
            }
        });

        // When parent is scaled, update stamp position
        parentObj.on('scaling', () => {
            if (parentObj.annotationStamp) {
                parentObj.annotationStamp.set({
                    left: parentObj.left,
                    top: parentObj.top + this.getStampOffset(parentObj),
                });
                parentObj.annotationStamp.setCoords();
            }
        });

        // When parent is removed, remove stamp too
        parentObj.on('removed', () => {
            if (parentObj.annotationStamp && this.fabricCanvas) {
                this.fabricCanvas.remove(parentObj.annotationStamp);
            }
        });

        this.fabricCanvas.add(stamp);
    }

    private getStampOffset(obj: any): number {
        if (obj.type === 'i-text' || obj.type === 'text') {
            // Use actual height which accounts for multiline text
            const actualHeight = obj.height * (obj.scaleY || 1);
            return actualHeight + 2;
        } else if (obj.type === 'rect') {
            return (obj.height || 0) * (obj.scaleY || 1) + 2;
        }
        return obj.annotationStamp?.stampOffsetY || 20;
    }

    private updateStampPosition(obj: any): void {
        if (obj.annotationStamp && this.fabricCanvas) {
            obj.annotationStamp.set({
                left: obj.left,
                top: obj.top + this.getStampOffset(obj),
            });
            obj.annotationStamp.setCoords();
            this.fabricCanvas.renderAll();
        }
    }

    private updateBackgroundSize(obj: any): void {
        if (!obj.backgroundRect || !this.fabricCanvas) return;

        const padding = 8;
        const actualTextWidth = obj.width * (obj.scaleX || 1);
        const actualTextHeight = obj.height * (obj.scaleY || 1);

        // Get stamp width if available
        const stampWidth = obj.annotationStamp?.width || 200;
        const stampHeight = obj.annotationStamp?.fontSize || 12;

        const totalHeight = actualTextHeight + stampHeight + 6; // Gap between text and stamp
        const bgWidth = Math.max(actualTextWidth, stampWidth) + padding * 2;

        obj.backgroundRect.set({
            left: obj.left - padding,
            top: obj.top - padding,
            width: bgWidth,
            height: totalHeight + padding * 2,
        });
        obj.backgroundRect.setCoords();
    }

    // Auto-placement functionality
    private autoPlaceText(text: string): void {
        if (!this.fabricCanvas || !this.$refs.pdfCanvas) return;

        const pdfCanvas = this.$refs.pdfCanvas as HTMLCanvasElement;
        const canvasWidth = pdfCanvas.width;
        const canvasHeight = pdfCanvas.height;

        // Find blank regions on the page
        const blankRegions = this.findBlankRegions(pdfCanvas);

        // Calculate max width for text (leave margin on sides)
        const maxTextWidth = canvasWidth - 60; // 30px margin on each side
        const charWidth = this.fontSize * 0.6;

        // Word-wrap text to fit within max width
        const wrappedText = this.wrapText(text, maxTextWidth, charWidth);
        const lines = wrappedText.split('\n');
        const maxLineLength = Math.max(...lines.map(l => l.length));
        const estimatedWidth = Math.min(maxLineLength * charWidth + 20, maxTextWidth);
        const estimatedHeight = (lines.length * this.fontSize * 1.2) + 30; // Include stamp height

        // Find best placement (prioritizes left-aligned, bottom of page)
        const placement = this.findBestPlacement(blankRegions, estimatedWidth, estimatedHeight, canvasWidth, canvasHeight);

        // Create text object with wrapped text
        const textObj = new fabric.IText(wrappedText, {
            left: placement.x,
            top: placement.y,
            fontSize: this.fontSize,
            fill: this.strokeColor,
            fontFamily: 'Arial',
            selectable: true,
        });

        // Add text change handlers
        textObj.on('changed', () => {
            this.updateStampPosition(textObj);
            this.updateBackgroundSize(textObj);
            this.fabricCanvas.renderAll();
        });

        textObj.on('editing:exited', () => {
            this.updateStampPosition(textObj);
            this.updateBackgroundSize(textObj);
        });

        this.fabricCanvas.add(textObj);

        // Add user stamp first so we can measure its dimensions
        this.addAnnotationStamp(textObj, this.getStampOffset(textObj));

        // If using fallback (no blank space found), add white background
        if (placement.needsBackground) {
            const padding = 8;

            // Calculate actual dimensions from the text object and stamp
            const actualTextWidth = textObj.width || estimatedWidth;
            const actualTextHeight = textObj.height || this.fontSize;
            const stampHeight = textObj.annotationStamp?.height || 16;
            const totalHeight = actualTextHeight + stampHeight + 4; // 4px gap between text and stamp

            // Get actual stamp width from the created stamp object
            const stampWidth = textObj.annotationStamp?.width || 200;
            const bgWidth = Math.max(actualTextWidth, stampWidth) + padding * 2;

            const bgRect = new fabric.Rect({
                left: placement.x - padding,
                top: placement.y - padding,
                width: bgWidth,
                height: totalHeight + padding * 2,
                fill: '#ffffff',
                stroke: '#cccccc',
                strokeWidth: 1,
                selectable: false,
                evented: false,
            });

            // Insert background behind text and stamp
            this.fabricCanvas.add(bgRect);
            bgRect.sendToBack();

            // Store reference so it moves with text
            textObj.backgroundRect = bgRect;

            // Update background position and size when text moves
            textObj.on('moving', () => {
                if (textObj.backgroundRect) {
                    textObj.backgroundRect.set({
                        left: textObj.left - padding,
                        top: textObj.top - padding,
                    });
                    textObj.backgroundRect.setCoords();
                }
            });

            textObj.on('removed', () => {
                if (textObj.backgroundRect && this.fabricCanvas) {
                    this.fabricCanvas.remove(textObj.backgroundRect);
                }
            });
        }

        // Select the text for editing
        this.fabricCanvas.setActiveObject(textObj);
        this.fabricCanvas.renderAll();

        // Auto-scroll to show the placed text
        this.scrollToObject(textObj);

        this.currentTool = 'select';
        this.hasUnsavedChanges = true;
        this.saveUndoState();
    }

    private wrapText(text: string, maxWidth: number, charWidth: number): string {
        const maxCharsPerLine = Math.floor(maxWidth / charWidth);
        const inputLines = text.split('\n');
        const outputLines: string[] = [];

        for (const line of inputLines) {
            if (line.length <= maxCharsPerLine) {
                outputLines.push(line);
            } else {
                // Word wrap long lines
                const words = line.split(' ');
                let currentLine = '';

                for (const word of words) {
                    if (currentLine.length === 0) {
                        currentLine = word;
                    } else if ((currentLine + ' ' + word).length <= maxCharsPerLine) {
                        currentLine += ' ' + word;
                    } else {
                        outputLines.push(currentLine);
                        currentLine = word;
                    }
                }

                if (currentLine.length > 0) {
                    outputLines.push(currentLine);
                }
            }
        }

        return outputLines.join('\n');
    }

    private scrollToObject(obj: any): void {
        if (!this.$refs.canvasWrapper) return;

        const wrapper = this.$refs.canvasWrapper as HTMLDivElement;
        const objTop = obj.top || 0;
        const objHeight = (obj.height || 0) * (obj.scaleY || 1);

        // Calculate scroll position to center the object in view
        const scrollTarget = objTop - (wrapper.clientHeight / 2) + (objHeight / 2);

        wrapper.scrollTo({
            top: Math.max(0, scrollTarget),
            behavior: 'smooth',
        });
    }

    private findBlankRegions(canvas: HTMLCanvasElement): Array<{ x: number; y: number; width: number; height: number }> {
        const ctx = canvas.getContext('2d');
        if (!ctx) return [];

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const pixels = imageData.data;

        const cellSize = 20; // Grid cell size in pixels
        const cols = Math.floor(canvas.width / cellSize);
        const rows = Math.floor(canvas.height / cellSize);

        // Build grid of blank/occupied cells
        const grid: boolean[][] = [];
        for (let row = 0; row < rows; row++) {
            grid[row] = [];
            for (let col = 0; col < cols; col++) {
                grid[row][col] = this.isCellBlank(pixels, col, row, cellSize, canvas.width);
            }
        }

        // Find contiguous blank rectangles using a simple algorithm
        return this.findBlankRectangles(grid, cellSize, cols, rows);
    }

    private isCellBlank(pixels: Uint8ClampedArray, col: number, row: number, cellSize: number, canvasWidth: number): boolean {
        const startX = col * cellSize;
        const startY = row * cellSize;
        const threshold = 230; // Near-white threshold (lowered to catch off-white backgrounds)

        let blankPixels = 0;
        let totalPixels = 0;

        for (let y = startY; y < startY + cellSize && y < pixels.length / (canvasWidth * 4); y++) {
            for (let x = startX; x < startX + cellSize && x < canvasWidth; x++) {
                const i = (y * canvasWidth + x) * 4;
                if (i + 2 < pixels.length) {
                    const r = pixels[i];
                    const g = pixels[i + 1];
                    const b = pixels[i + 2];

                    if (r > threshold && g > threshold && b > threshold) {
                        blankPixels++;
                    }
                    totalPixels++;
                }
            }
        }

        return totalPixels > 0 && (blankPixels / totalPixels) > 0.90; // 90% blank = blank cell
    }

    private findBlankRectangles(grid: boolean[][], cellSize: number, cols: number, rows: number): Array<{ x: number; y: number; width: number; height: number }> {
        const regions: Array<{ x: number; y: number; width: number; height: number }> = [];
        const visited: boolean[][] = grid.map(row => row.map(() => false));

        for (let startRow = 0; startRow < rows; startRow++) {
            for (let startCol = 0; startCol < cols; startCol++) {
                if (grid[startRow][startCol] && !visited[startRow][startCol]) {
                    // Find max width of blank cells starting from this position
                    let maxWidth = 0;
                    for (let col = startCol; col < cols && grid[startRow][col] && !visited[startRow][col]; col++) {
                        maxWidth++;
                    }

                    // Find max height with this width
                    let maxHeight = 1;
                    for (let row = startRow + 1; row < rows; row++) {
                        let rowValid = true;
                        for (let col = startCol; col < startCol + maxWidth; col++) {
                            if (!grid[row][col] || visited[row][col]) {
                                rowValid = false;
                                break;
                            }
                        }
                        if (rowValid) {
                            maxHeight++;
                        } else {
                            break;
                        }
                    }

                    // Mark cells as visited
                    for (let row = startRow; row < startRow + maxHeight; row++) {
                        for (let col = startCol; col < startCol + maxWidth; col++) {
                            visited[row][col] = true;
                        }
                    }

                    // Only add if region is large enough (at least 3x3 cells)
                    if (maxWidth >= 3 && maxHeight >= 2) {
                        regions.push({
                            x: startCol * cellSize,
                            y: startRow * cellSize,
                            width: maxWidth * cellSize,
                            height: maxHeight * cellSize,
                        });
                    }
                }
            }
        }

        return regions;
    }

    private findBestPlacement(
        _regions: Array<{ x: number; y: number; width: number; height: number }>,
        textWidth: number,
        textHeight: number,
        canvasWidth: number,
        canvasHeight: number
    ): { x: number; y: number; needsBackground: boolean } {
        // Calculate total height needed (text + stamp + box padding)
        const bottomMargin = 20;
        const boxPadding = 16; // 8px padding on top and bottom of background box
        const stampHeight = 20; // Approximate stamp height with spacing
        const totalHeight = textHeight + boxPadding + stampHeight;
        const maxY = canvasHeight - totalHeight - bottomMargin;

        // Width needed for the annotation (text + stamp width, whichever is larger)
        const neededWidth = Math.max(textWidth, 250) + boxPadding * 2; // 250 is approx stamp width

        // Find a blank area that can fit the annotation
        const blankArea = this.findBlankArea(20, neededWidth, totalHeight, canvasWidth, canvasHeight);

        if (blankArea) {
            return {
                x: 20,
                y: blankArea.y,
                needsBackground: !blankArea.isClean, // Add background if area has some content
            };
        }

        // Fallback: place at the very bottom with background
        return {
            x: 20,
            y: Math.max(maxY, 20),
            needsBackground: true,
        };
    }

    private findBlankArea(
        startX: number,
        neededWidth: number,
        neededHeight: number,
        canvasWidth: number,
        canvasHeight: number
    ): { y: number; isClean: boolean } | null {
        const pdfCanvas = this.$refs.pdfCanvas as HTMLCanvasElement;
        if (!pdfCanvas) return null;

        const ctx = pdfCanvas.getContext('2d');
        if (!ctx) return null;

        const imageData = ctx.getImageData(0, 0, canvasWidth, canvasHeight);
        const pixels = imageData.data;
        const threshold = 248; // Very high threshold to catch even light gray

        // Scan from top to find where main content starts, then find blank areas after
        // First, find where the main content block ends
        let mainContentEndY = 0;
        let consecutiveBlankRows = 0;
        const blankRowsNeeded = 10; // Need 10 consecutive blank rows to consider content ended

        for (let y = 0; y < canvasHeight; y++) {
            const rowHasContent = this.rowHasContent(pixels, y, startX, Math.min(startX + neededWidth, canvasWidth - 10), canvasWidth, threshold);

            if (rowHasContent) {
                mainContentEndY = y;
                consecutiveBlankRows = 0;
            } else {
                consecutiveBlankRows++;
                // Once we've found the main content and have enough blank rows, look for placement
                if (mainContentEndY > 50 && consecutiveBlankRows >= blankRowsNeeded) {
                    // Check if we can fit the annotation here
                    const checkY = mainContentEndY + 15; // 15px margin below content
                    if (checkY + neededHeight <= canvasHeight - 20) {
                        const areaIsClean = this.checkAreaIsBlank(
                            pixels, checkY, startX,
                            Math.min(startX + neededWidth, canvasWidth - 10),
                            neededHeight, canvasWidth, threshold
                        );
                        if (areaIsClean) {
                            return { y: checkY, isClean: true };
                        }
                    }
                }
            }
        }

        // If we found main content, place below it
        if (mainContentEndY > 0) {
            const placementY = mainContentEndY + 20;
            if (placementY + neededHeight <= canvasHeight - 20) {
                return { y: placementY, isClean: false };
            }
        }

        return null;
    }

    private rowHasContent(
        pixels: Uint8ClampedArray,
        y: number,
        startX: number,
        endX: number,
        canvasWidth: number,
        threshold: number
    ): boolean {
        for (let x = startX; x < endX; x += 2) {
            const i = (y * canvasWidth + x) * 4;
            if (i + 2 < pixels.length) {
                const r = pixels[i];
                const g = pixels[i + 1];
                const b = pixels[i + 2];
                if (r < threshold || g < threshold || b < threshold) {
                    return true;
                }
            }
        }
        return false;
    }

    private checkAreaIsBlank(
        pixels: Uint8ClampedArray,
        startY: number,
        startX: number,
        endX: number,
        height: number,
        canvasWidth: number,
        threshold: number
    ): boolean {
        for (let y = startY; y < startY + height && y < pixels.length / (canvasWidth * 4); y += 3) {
            if (this.rowHasContent(pixels, y, startX, endX, canvasWidth, threshold)) {
                return false;
            }
        }
        return true;
    }

    private findContentEndY(canvasWidth: number, canvasHeight: number): number {
        const pdfCanvas = this.$refs.pdfCanvas as HTMLCanvasElement;
        if (!pdfCanvas) return canvasHeight - 100;

        const ctx = pdfCanvas.getContext('2d');
        if (!ctx) return canvasHeight - 100;

        const imageData = ctx.getImageData(0, 0, canvasWidth, canvasHeight);
        const pixels = imageData.data;
        const threshold = 245; // Higher threshold to catch lighter gray text/annotations
        const sampleStepX = 3; // Sample every 3 pixels horizontally to catch thin text
        const sampleStepY = 2; // Sample every 2 pixels vertically for accuracy

        // Scan from bottom up to find where content ends
        // Check nearly full width to catch annotations anywhere on the page
        for (let y = canvasHeight - 10; y >= 0; y -= sampleStepY) {
            let hasContent = false;

            // Scan almost full width (leave small margins)
            for (let x = 10; x < canvasWidth - 10; x += sampleStepX) {
                const i = (y * canvasWidth + x) * 4;
                if (i + 2 < pixels.length) {
                    const r = pixels[i];
                    const g = pixels[i + 1];
                    const b = pixels[i + 2];

                    // If pixel is not near-white, it's content
                    // Using higher threshold to catch gray text and annotations
                    if (r < threshold || g < threshold || b < threshold) {
                        hasContent = true;
                        break;
                    }
                }
            }

            if (hasContent) {
                // Found content, return position just below it
                return Math.min(y + 25, canvasHeight - 50);
            }
        }

        // No content found (blank page), place near top
        return 50;
    }

    private onMouseDown(e: any): void {
        if (!this.hasPermission) return;

        // For text tool, allow placing text anywhere (even on top of other objects)
        if (this.currentTool === 'text') {
            // If clicking on an existing text object, let Fabric handle it for editing
            if (e.target && (e.target.type === 'i-text' || e.target.type === 'text')) {
                return;
            }

            // Deselect any current selection
            this.fabricCanvas.discardActiveObject();

            const colors = this.getCurrentColors();
            const pointer = this.fabricCanvas.getPointer(e.e);
            const text = new fabric.IText('Click to edit', {
                left: pointer.x,
                top: pointer.y,
                fontSize: this.fontSize,
                fill: colors.stroke,
                fontFamily: 'Arial',
                selectable: true,
            });

            // Clear placeholder text when user starts typing and update stamp position for multiline
            text.on('changed', () => {
                if (text.text === 'Click to edit') {
                    text.text = '';
                }
                // Update stamp position when text content changes (e.g., multiline)
                this.updateStampPosition(text);
                this.fabricCanvas.renderAll();
            });

            // Also clear on first keydown if placeholder is still there
            text.on('editing:entered', () => {
                if (text.text === 'Click to edit') {
                    text.selectAll();
                }
            });

            // Update stamp when exiting edit mode (final position)
            text.on('editing:exited', () => {
                this.updateStampPosition(text);
            });

            this.fabricCanvas.add(text);

            // Add username/datetime stamp linked to this text
            this.addAnnotationStamp(text, this.fontSize + 2);

            this.fabricCanvas.setActiveObject(text);
            text.enterEditing();

            // Switch to select tool after placing text
            this.setTool('select');
        } else if (this.currentTool === 'rect' && !e.target) {
            const colors = this.getCurrentColors();
            this.isDrawingRect = true;
            this.rectStartPoint = this.fabricCanvas.getPointer(e.e);
            this.tempRect = new fabric.Rect({
                left: this.rectStartPoint.x,
                top: this.rectStartPoint.y,
                width: 0,
                height: 0,
                fill: colors.noFill ? 'transparent' : colors.fill,
                stroke: colors.stroke,
                strokeWidth: 2,
                selectable: true,
                evented: true,
            });
            this.fabricCanvas.add(this.tempRect);
            this.fabricCanvas.selection = false;
        }
    }

    private onMouseMove(e: any): void {
        if (!this.isDrawingRect || !this.tempRect || !this.rectStartPoint) return;

        const pointer = this.fabricCanvas.getPointer(e.e);
        const left = Math.min(pointer.x, this.rectStartPoint.x);
        const top = Math.min(pointer.y, this.rectStartPoint.y);
        const width = Math.abs(pointer.x - this.rectStartPoint.x);
        const height = Math.abs(pointer.y - this.rectStartPoint.y);

        this.tempRect.set({ left, top, width, height });
        this.fabricCanvas.renderAll();
    }

    private onMouseUp(): void {
        if (this.isDrawingRect && this.tempRect) {
            if (this.tempRect.width < 5 && this.tempRect.height < 5) {
                this.fabricCanvas.remove(this.tempRect);
            } else {
                this.tempRect.setCoords();
                this.fabricCanvas.setActiveObject(this.tempRect);

                // Add username/datetime stamp linked to this rectangle
                this.addAnnotationStamp(this.tempRect, this.tempRect.height + 2);

                this.saveUndoState();
            }

            this.isDrawingRect = false;
            this.rectStartPoint = null;
            this.tempRect = null;
            this.fabricCanvas.selection = true;
            this.fabricCanvas.renderAll();
        }
    }

    private setTool(tool: string): void {
        this.currentTool = tool;
        this.isDrawingRect = false;
        this.rectStartPoint = null;
        this.tempRect = null;

        if (!this.fabricCanvas) return;

        this.fabricCanvas.selection = true;

        if (tool === 'select') {
            this.fabricCanvas.defaultCursor = 'default';
            this.fabricCanvas.hoverCursor = 'move';
        } else {
            this.fabricCanvas.defaultCursor = 'crosshair';
            this.fabricCanvas.hoverCursor = 'crosshair';
        }
    }

    private deleteSelected(): void {
        if (!this.fabricCanvas || !this.hasPermission) return;

        const activeObjects = this.fabricCanvas.getActiveObjects();
        if (activeObjects.length > 0) {
            activeObjects.forEach((obj: any) => {
                this.fabricCanvas.remove(obj);
            });
            this.fabricCanvas.discardActiveObject();
            this.fabricCanvas.renderAll();
        }
    }

    private applyColorToSelection(): void {
        const obj = this.fabricCanvas?.getActiveObject();
        if (!obj) return;

        if (obj.type === 'i-text' || obj.type === 'text') {
            obj.set('fill', this.strokeColor);
        } else {
            obj.set('stroke', this.strokeColor);
        }
        this.fabricCanvas.renderAll();
        this.saveUndoState();
    }

    private applyFillToSelection(): void {
        const obj = this.fabricCanvas?.getActiveObject();
        if (!obj || obj.type !== 'rect') return;

        obj.set('fill', this.noFill ? 'transparent' : this.fillColor);
        this.fabricCanvas.renderAll();
        this.saveUndoState();
    }

    private applyFontSizeToSelection(): void {
        const obj = this.fabricCanvas?.getActiveObject();
        if (!obj || (obj.type !== 'i-text' && obj.type !== 'text')) return;

        obj.set('fontSize', this.fontSize);
        this.fabricCanvas.renderAll();

        // Update stamp font size and position after parent font size change
        if (obj.annotationStamp) {
            const newStampFontSize = Math.max(12, Math.round(this.fontSize * 0.6));
            obj.annotationStamp.set('fontSize', newStampFontSize);
        }
        this.updateStampPosition(obj);

        this.saveUndoState();
    }

    // Layer functions
    private bringToFront(): void {
        const obj = this.fabricCanvas?.getActiveObject();
        if (obj) {
            this.fabricCanvas.bringToFront(obj);
            this.fabricCanvas.renderAll();
            this.saveUndoState();
        }
    }

    private bringForward(): void {
        const obj = this.fabricCanvas?.getActiveObject();
        if (obj) {
            this.fabricCanvas.bringForward(obj);
            this.fabricCanvas.renderAll();
            this.saveUndoState();
        }
    }

    private sendBackward(): void {
        const obj = this.fabricCanvas?.getActiveObject();
        if (obj) {
            this.fabricCanvas.sendBackward(obj);
            this.fabricCanvas.renderAll();
            this.saveUndoState();
        }
    }

    private sendToBack(): void {
        const obj = this.fabricCanvas?.getActiveObject();
        if (obj) {
            this.fabricCanvas.sendToBack(obj);
            this.fabricCanvas.renderAll();
            this.saveUndoState();
        }
    }

    // Undo functionality
    private saveUndoState(): void {
        if (!this.fabricCanvas || this.isUndoing) return;

        if (!this.undoStacks[this.currentPage]) {
            this.undoStacks[this.currentPage] = [];
        }

        const state = JSON.stringify(this.fabricCanvas.toJSON());
        const stack = this.undoStacks[this.currentPage];

        if (stack.length > 0 && stack[stack.length - 1] === state) {
            return;
        }

        // Mark as having unsaved changes if this isn't the initial state
        if (stack.length > 0) {
            this.hasUnsavedChanges = true;
        }

        stack.push(state);

        if (stack.length > this.MAX_UNDO_STATES) {
            stack.shift();
        }
    }

    private undo(): void {
        if (!this.fabricCanvas) return;

        const stack = this.undoStacks[this.currentPage];
        if (!stack || stack.length <= 1) {
            return;
        }

        this.isUndoing = true;
        stack.pop();
        const previousState = stack[stack.length - 1];

        this.fabricCanvas.loadFromJSON(previousState, () => {
            this.fabricCanvas.renderAll();
            this.isUndoing = false;
        });
    }

    private goToPage(pageNum: number): void {
        if (pageNum >= 1 && pageNum <= this.totalPages) {
            this.renderPage(pageNum);
        }
    }

    // Keyboard shortcuts
    private keydownHandler: ((e: KeyboardEvent) => void) | null = null;

    private setupKeyboardShortcuts(): void {
        this.keydownHandler = (e: KeyboardEvent) => {
            // Don't intercept any keys when editing text
            const activeObject = this.fabricCanvas?.getActiveObject();
            if (activeObject && activeObject.type === 'i-text' && activeObject.isEditing) {
                return;
            }

            // Ctrl+Z for undo
            if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
                e.preventDefault();
                this.undo();
                return;
            }

            // Delete for delete
            if ((e.key === 'Delete' || e.key === 'Backspace') && this.fabricCanvas) {
                if (activeObject) {
                    e.preventDefault();
                    this.deleteSelected();
                }
            }
        };

        document.addEventListener('keydown', this.keydownHandler);
    }

    private removeKeyboardShortcuts(): void {
        if (this.keydownHandler) {
            document.removeEventListener('keydown', this.keydownHandler);
            this.keydownHandler = null;
        }
    }

    // Remove placeholder text objects that weren't edited
    private removePlaceholderTexts(): void {
        if (!this.fabricCanvas) return;

        const objects = this.fabricCanvas.getObjects();
        const toRemove: any[] = [];

        for (const obj of objects) {
            if ((obj.type === 'i-text' || obj.type === 'text') && obj.text === 'Click to edit') {
                toRemove.push(obj);
            }
        }

        // Remove each placeholder (event handlers will clean up stamps and backgrounds)
        for (const obj of toRemove) {
            this.fabricCanvas.remove(obj);
        }

        if (toRemove.length > 0) {
            this.fabricCanvas.renderAll();
        }
    }

    // Save functionality
    private async saveAnnotatedPdf(): Promise<void> {
        if (!this.pdfBytes || !this.hasPermission) return;

        this.saving = true;

        try {
            // Remove any placeholder text objects that weren't edited
            this.removePlaceholderTexts();

            // Save current page annotations
            if (this.fabricCanvas) {
                this.pageAnnotations[this.currentPage] = this.fabricCanvas.toJSON();
            }

            // Load the original PDF with pdf-lib
            const { PDFDocument, rgb, StandardFonts } = PDFLib;
            const pdfLibDoc = await PDFDocument.load(this.pdfBytes, {
                ignoreEncryption: true,
            });

            const helveticaFont = await pdfLibDoc.embedFont(StandardFonts.Helvetica);
            const pages = pdfLibDoc.getPages();

            // Process each page with annotations
            for (let pageNum = 1; pageNum <= this.totalPages; pageNum++) {
                if (!this.pageAnnotations[pageNum]) continue;

                const page = pages[pageNum - 1];
                const { width, height } = page.getSize();
                const annotations = this.pageAnnotations[pageNum];

                // Scale factor between Fabric canvas and PDF page
                const canvasWidth = this.fabricCanvas?.width || width * this.scale;
                const canvasHeight = this.fabricCanvas?.height || height * this.scale;
                const scaleX = width / canvasWidth;
                const scaleY = height / canvasHeight;

                for (const obj of annotations.objects || []) {
                    const hexToRgb = (hex: string) => {
                        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '#000000');
                        return result
                            ? {
                                  r: parseInt(result[1], 16) / 255,
                                  g: parseInt(result[2], 16) / 255,
                                  b: parseInt(result[3], 16) / 255,
                              }
                            : { r: 0, g: 0, b: 0 };
                    };

                    const pdfX = (obj.left || 0) * scaleX;
                    const pdfY = height - (obj.top || 0) * scaleY;
                    const objScaleX = obj.scaleX || 1;
                    const objScaleY = obj.scaleY || 1;

                    switch (obj.type) {
                        case 'i-text':
                        case 'text': {
                            const textColor = hexToRgb(obj.fill || '#000000');
                            const fontSize = (obj.fontSize || 16) * scaleY * objScaleY;
                            // Fabric.js default lineHeight is 1.16, match it for PDF rendering
                            const lineHeight = (obj.lineHeight || 1.16) * fontSize;
                            const textContent = obj.text || '';
                            const lines = textContent.split('\n');

                            // Draw each line separately with correct line spacing
                            lines.forEach((line: string, index: number) => {
                                const lineY = pdfY - fontSize - (index * lineHeight);
                                page.drawText(line, {
                                    x: pdfX,
                                    y: lineY,
                                    size: fontSize,
                                    font: helveticaFont,
                                    color: rgb(textColor.r, textColor.g, textColor.b),
                                });
                            });
                            break;
                        }

                        case 'rect': {
                            const rectW = (obj.width || 0) * objScaleX * scaleX;
                            const rectH = (obj.height || 0) * objScaleY * scaleY;
                            const rectStrokeColor = hexToRgb(obj.stroke || '#000000');
                            const rectOptions: any = {
                                x: pdfX,
                                y: pdfY - rectH,
                                width: rectW,
                                height: rectH,
                                borderColor: rgb(rectStrokeColor.r, rectStrokeColor.g, rectStrokeColor.b),
                                borderWidth: obj.strokeWidth || 2,
                            };

                            if (obj.fill && obj.fill !== 'transparent') {
                                const rectFillColor = hexToRgb(obj.fill);
                                rectOptions.color = rgb(rectFillColor.r, rectFillColor.g, rectFillColor.b);
                            }

                            page.drawRectangle(rectOptions);
                            break;
                        }
                    }
                }
            }

            // Save the modified PDF
            const modifiedPdfBytes = await pdfLibDoc.save();
            const blob = new Blob([modifiedPdfBytes], { type: 'application/pdf' });

            // Upload to API - different endpoint for eScript vs regular image
            const formData = new FormData();

            if (this.escriptId) {
                // For eScripts, update the existing eScript image
                formData.append('file', blob, 'annotated.pdf');
                await axios.put(`/api/Escript/${this.escriptId}/image`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });

                this.hasUnsavedChanges = false;
                this.$notification(NotificationOptions.success('eScript annotation saved successfully.'));
                this.emitSaved({ oldImageId: null, newImageId: 0, escriptId: this.escriptId });
                this.$bvModal.hide(this.modalId);
            } else {
                // For regular images, create a new image document
                formData.append('files', blob, 'annotated.pdf');
                const response = await axios.post('/api/Image/SaveDocument?bypassIngestion=true', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });

                const imageIds = response.data as number[];
                if (imageIds && imageIds.length > 0) {
                    const newImageId = imageIds[0];
                    this.hasUnsavedChanges = false;
                    this.$notification(NotificationOptions.success('PDF saved successfully.'));
                    this.emitSaved({ oldImageId: this.imageId, newImageId, escriptId: null });
                    this.$bvModal.hide(this.modalId);
                } else {
                    throw new Error('No image ID returned from server.');
                }
            }
        } catch (err: any) {
            console.error('Error saving PDF:', err);
            this.$notification(
                NotificationOptions.error(err?.response?.data?.message || err?.message || 'Failed to save PDF.')
            );
        } finally {
            this.saving = false;
        }
    }

    // Close handling
    private handleCloseRequest(): void {
        if (this.hasUnsavedChanges) {
            this.showUnsavedWarning = true;
        } else {
            this.$bvModal.hide(this.modalId);
        }
    }

    private discardAndClose(): void {
        this.hasUnsavedChanges = false;
        this.showUnsavedWarning = false;
        this.$bvModal.hide(this.modalId);
    }

    // Public method to open the modal
    public show(): void {
        this.$bvModal.show(this.modalId);
    }

    // Public method to hide the modal
    public hide(): void {
        this.handleCloseRequest();
    }
}
</script>

<style scoped>
.canvas-container-wrapper {
    overflow: auto;
    max-height: calc(100vh - 250px);
    border: 1px solid #dee2e6;
    border-radius: 4px;
    background: #f8f9fa;
}

.canvas-container {
    position: relative;
    display: inline-block;
    margin: auto;
}

.canvas-container canvas {
    display: block;
}

.color-picker {
    width: 40px;
    height: 30px;
    padding: 2px;
    border: 1px solid #343a40;
    cursor: pointer;
}

.annotation-toolbar {
    border: 1px solid #343a40;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.annotation-toolbar .btn-group {
    flex-shrink: 0;
}

.annotation-toolbar .btn-outline-dark {
    border-color: #343a40;
    color: #343a40;
}

.annotation-toolbar .btn-outline-dark:hover:not(:disabled) {
    background-color: #343a40;
    color: #fff;
}

.annotation-toolbar .btn-outline-dark:disabled {
    opacity: 0.4;
}

.annotation-toolbar label {
    color: #343a40;
    font-weight: 500;
}

.font-size-select {
    width: 65px;
    border-color: #343a40;
}
</style>
